import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase';
import TicketCard from '../components/TicketCard';
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  query,
  orderBy
} from 'firebase/firestore';

const Admin = () => {
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [passcode, setPasscode] = useState('');
  const [passcodeError, setPasscodeError] = useState('');

  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all'); // all, pending, confirmed
  const [selectedReg, setSelectedReg] = useState(null);

  // Check auth status on mount
  useEffect(() => {
    const authStatus = sessionStorage.getItem('admin_authorized');
    if (authStatus === 'true') {
      setIsAuthorized(true);
    }
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();
    if (passcode.trim().toUpperCase() === 'LLC2026' || passcode.trim() === '2026') {
      sessionStorage.setItem('admin_authorized', 'true');
      setIsAuthorized(true);
      setPasscodeError('');
    } else {
      setPasscodeError('Invalid passcode. Hint: Use LLC2026');
    }
  };

  // Fetch registrations directly from Firebase Firestore
  const fetchRegistrations = useCallback(async () => {
    if (!db) {
      setError('Firebase Firestore database instance (db) is not initialized. Please verify configuration in src/firebase.js');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const q = query(collection(db, 'registrations'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      const list = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data()
      }));
      setRegistrations(list);
    } catch (err) {
      console.error('Failed to fetch registrations from Firestore:', err);
      setError(`Firestore Fetch Error: ${err.message}. Please check your Firestore Security Rules.`);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch when authorized
  useEffect(() => {
    if (isAuthorized) {
      fetchRegistrations();
    }
  }, [isAuthorized, fetchRegistrations]);

  // Update registration status
  const handleUpdateStatus = async (id, newStatus) => {
    if (db && doc && updateDoc) {
      try {
        const docRef = doc(db, 'registrations', id);
        await updateDoc(docRef, { status: newStatus });
        // Refresh local state list
        setRegistrations(prev =>
          prev.map(reg => reg.id === id ? { ...reg, status: newStatus } : reg)
        );
      } catch (err) {
        alert('Failed to update registration status: ' + err.message);
      }
    }
  };

  // Delete registration
  const handleDeleteRegistration = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete the registration for ${name}?`)) {
      return;
    }

    if (db && doc && deleteDoc) {
      try {
        const docRef = doc(db, 'registrations', id);
        await deleteDoc(docRef);
        setRegistrations(prev => prev.filter(reg => reg.id !== id));
      } catch (err) {
        alert('Failed to delete registration: ' + err.message);
      }
    }
  };

  // Calculate statistics
  const stats = React.useMemo(() => {
    const total = registrations.length;
    const confirmed = registrations.filter(r => r.status === 'confirmed').length;
    const pending = registrations.filter(r => r.status === 'pending').length;
    const revenue = registrations
      .filter(r => r.status === 'confirmed')
      .reduce((acc, r) => acc + (r.ticketType === 'vip' ? 30000 : 10000), 0);

    return { total, confirmed, pending, revenue };
  }, [registrations]);

  // Filter registrations based on active tab and search query
  const filteredRegistrations = React.useMemo(() => {
    return registrations.filter(r => {
      // Tab filter
      if (activeTab === 'pending' && r.status !== 'pending') return false;
      if (activeTab === 'confirmed' && r.status !== 'confirmed') return false;

      // Search filter
      if (searchQuery.trim() !== '') {
        const queryVal = searchQuery.toLowerCase();
        const nameMatch = r.name?.toLowerCase().includes(queryVal);
        const emailMatch = r.email?.toLowerCase().includes(queryVal);
        const phoneMatch = r.phone?.toLowerCase().includes(queryVal);
        const ticketIdMatch = r.ticketId?.toLowerCase().includes(queryVal);

        return nameMatch || emailMatch || phoneMatch || ticketIdMatch;
      }

      return true;
    });
  }, [registrations, activeTab, searchQuery]);

  // Passcode gate view
  if (!isAuthorized) {
    return (
      <div className="admin-gate">
        <div className="admin-gate-card">
          <div className="admin-gate-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
            </svg>
          </div>
          <h2 className="admin-gate-title">Admin Dashboard</h2>
          <p className="admin-gate-text">Enter the admin passcode to validate attendee tickets.</p>
          <form onSubmit={handleLogin}>
            <div className="admin-gate-input-group">
              <input
                type="password"
                className="form-input"
                placeholder="Passcode"
                value={passcode}
                onChange={(e) => setPasscode(e.target.value)}
                autoFocus
                required
              />
              {passcodeError && <div className="admin-gate-error">{passcodeError}</div>}
            </div>
            <button type="submit" className="form-submit">Unlock Dashboard</button>
          </form>
        </div>
      </div>
    );
  }

  // Dashboard content view
  return (
    <div className="admin-dashboard">
      <div className="container">

        {/* Header */}
        <div className="admin-dashboard__header">
          <div>
            <h1 className="admin-dashboard__title">
              LLC 2026 <span>Dashboard</span>
            </h1>
            <p style={{ color: '#6b7280', fontSize: '14px', marginTop: '4px' }}>
              Ticket Validation Panel
            </p>
          </div>
          <div className="admin-actions">
            <button
              className="btn btn-outline btn-sm"
              onClick={fetchRegistrations}
              disabled={loading}
            >
              {loading ? 'Refreshing...' : 'Refresh Data'}
            </button>
            <button
              className="btn btn-outline-red btn-sm"
              onClick={() => {
                sessionStorage.removeItem('admin_authorized');
                setIsAuthorized(false);
              }}
            >
              Logout
            </button>
          </div>
        </div>

        {error && (
          <div className="ticket-check-result ticket-check-result--error" style={{ marginBottom: '24px', textAlign: 'left' }}>
            ⚠️ {error}
          </div>
        )}

        {/* Stats Row */}
        <div className="admin-stats">
          <div className="admin-stat-card">
            <span className="admin-stat-label">Total Registered</span>
            <span className="admin-stat-val">{stats.total}</span>
          </div>
          <div className="admin-stat-card admin-stat-card--confirmed">
            <span className="admin-stat-label">Confirmed Passes</span>
            <span className="admin-stat-val" style={{ color: 'var(--green)' }}>{stats.confirmed}</span>
          </div>
          <div className="admin-stat-card admin-stat-card--pending">
            <span className="admin-stat-label">Pending Payment</span>
            <span className="admin-stat-val" style={{ color: 'var(--red)' }}>{stats.pending}</span>
          </div>
          <div className="admin-stat-card admin-stat-card--revenue">
            <span className="admin-stat-label">Estimated Revenue</span>
            <span className="admin-stat-val" style={{ color: 'var(--yellow)' }}>
              {stats.revenue.toLocaleString()} <span style={{ fontSize: '14px', fontWeight: '700' }}>FCFA</span>
            </span>
          </div>
        </div>

        {/* Controls: Search & Tabs */}
        <div className="admin-controls">
          <div className="admin-search-bar">
            <input
              type="text"
              className="form-input"
              placeholder="Search by ticket ID, name, or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                className="btn btn-outline"
                style={{ borderRadius: 'var(--radius)', padding: '10px 14px' }}
                onClick={() => setSearchQuery('')}
              >
                Clear
              </button>
            )}
          </div>
          <div className="admin-tabs">
            <button
              className={`admin-tab ${activeTab === 'all' ? 'admin-tab--active' : ''}`}
              onClick={() => setActiveTab('all')}
            >
              All ({registrations.length})
            </button>
            <button
              className={`admin-tab ${activeTab === 'pending' ? 'admin-tab--active' : ''}`}
              onClick={() => setActiveTab('pending')}
            >
              Pending ({registrations.filter(r => r.status === 'pending').length})
            </button>
            <button
              className={`admin-tab ${activeTab === 'confirmed' ? 'admin-tab--active' : ''}`}
              onClick={() => setActiveTab('confirmed')}
            >
              Confirmed ({registrations.filter(r => r.status === 'confirmed').length})
            </button>
          </div>
        </div>

        {/* Registrations List */}
        <div className="admin-table-container">
          {loading ? (
            <div style={{ textAlign: 'center', padding: '48px', color: 'var(--text-muted)' }}>
              <h3>Loading registrations...</h3>
            </div>
          ) : filteredRegistrations.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px', color: 'var(--text-muted)' }}>
              <h3>No registrations found</h3>
              <p style={{ marginTop: '8px', fontSize: '14px' }}>Try adjusting your filters or query.</p>
            </div>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Ticket ID</th>
                  <th>Attendee Info</th>
                  <th>Contact Details</th>
                  <th>Pass Type</th>
                  <th>Status</th>
                  <th>Registered At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRegistrations.map((reg) => (
                  <tr key={reg.id}>
                    <td style={{ fontFamily: 'monospace', fontWeight: '700', fontSize: '15px', color: 'var(--blue)' }}>
                      {reg.ticketId}
                    </td>
                    <td>
                      <div style={{ fontWeight: '700', color: 'var(--text-dark)' }}>{reg.name}</div>
                      {reg.linkedin && (
                        <a
                          href={reg.linkedin}
                          target="_blank"
                          rel="noreferrer"
                          style={{ fontSize: '12px', color: 'var(--blue)', textDecoration: 'underline' }}
                        >
                          LinkedIn Profile
                        </a>
                      )}
                    </td>
                    <td>
                      <div>{reg.email}</div>
                      <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>{reg.phone}</div>
                    </td>
                    <td>
                      <span className={`admin-badge admin-badge--${reg.ticketType}`}>
                        {reg.ticketType === 'vip' ? 'VIP' : 'Delegate'}
                      </span>
                    </td>
                    <td>
                      <span className={`admin-badge admin-badge--${reg.status}`}>
                        {reg.status === 'confirmed' ? 'Confirmed' : 'Pending'}
                      </span>
                    </td>
                    <td style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
                      {reg.createdAt ? new Date(reg.createdAt).toLocaleDateString() : 'N/A'}
                    </td>
                    <td>
                      <div className="admin-actions">
                        {reg.status === 'pending' ? (
                          <button
                            className="admin-btn admin-btn--confirm"
                            onClick={() => handleUpdateStatus(reg.id, 'confirmed')}
                          >
                            Validate
                          </button>
                        ) : (
                          <button
                            className="admin-btn admin-btn--pending"
                            onClick={() => handleUpdateStatus(reg.id, 'pending')}
                          >
                            Mark Pending
                          </button>
                        )}
                        <button
                          className="admin-btn admin-btn--ticket"
                          onClick={() => setSelectedReg(reg)}
                          title="Download/View Ticket"
                        >
                          Ticket
                        </button>
                        <button
                          className="admin-btn admin-btn--delete"
                          onClick={() => handleDeleteRegistration(reg.id, reg.name)}
                          title="Delete Attendee"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

      </div>

      {/* TICKET DOWNLOAD MODAL */}
      {selectedReg && (
        <div className="admin-modal-overlay" onClick={() => setSelectedReg(null)}>
          <div className="admin-modal-card" onClick={(e) => e.stopPropagation()}>
            <button className="admin-modal-close" onClick={() => setSelectedReg(null)} aria-label="Close">
              ✕
            </button>
            <h3 style={{ fontFamily: 'Montserrat, sans-serif', fontSize: '20px', fontWeight: 800, color: 'var(--text-dark)', marginBottom: '24px', textTransform: 'uppercase' }}>
              Ticket Preview: <span style={{ color: 'var(--blue)' }}>{selectedReg.name}</span>
            </h3>
            <TicketCard
              ticketData={{
                name: selectedReg.name,
                email: selectedReg.email,
                phone: selectedReg.phone,
                ticketType: selectedReg.ticketType || 'earlybird',
                ticketId: selectedReg.ticketId,
                photo: null,
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
